import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Clock, RefreshCw, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { authService } from '@/services/auth-service'
import { logInfo, logError } from '@/lib/logger'

interface SessionExpiryWarningProps {
  open: boolean
  onDismiss: () => void
}

export function SessionExpiryWarning({ open, onDismiss }: SessionExpiryWarningProps) {
  const { t } = useTranslation()
  const [extending, setExtending] = useState(false)

  const handleExtendSession = useCallback(async () => {
    setExtending(true)
    // Dismiss optimistically — if the refresh fails with InteractionRequiredAuthError
    // the auth service emits 'auth-session-expired', which shows the re-login modal.
    // Without early dismiss, MSAL occasionally returns the same cached token (same
    // expiresOn) despite forceRefresh:true; scheduleTokenRefresh() then recomputes a
    // 0-ms delay and re-fires 'auth-session-expiring' in the same tick, causing the
    // banner to flash dismiss → reappear.
    onDismiss()
    try {
      await authService.refreshAccessToken(true)
      logInfo({ context: 'SessionExpiryWarning' }, 'Session extended by user')
    } catch (err) {
      logError(err instanceof Error ? err : new Error(String(err)), {
        context: 'SessionExpiryWarning',
      })
    } finally {
      setExtending(false)
    }
  }, [onDismiss])

  if (!open) return null

  return (
    <div className="fixed top-0 left-0 right-0 z-[15000] flex justify-center pointer-events-none">
      <div className="m-4 flex items-center gap-3 rounded-lg border border-[var(--window-border-color)] bg-[var(--window-background)] px-4 py-3 shadow-lg backdrop-blur-sm pointer-events-auto">
        <Clock className="h-5 w-5 text-[var(--text-secondary)] flex-shrink-0" />
        <span className="text-sm font-medium text-[var(--text-color)]">
          {t('auth.sessionExpiringMessage')}
        </span>
        <Button
          size="sm"
          variant="outline"
          onClick={handleExtendSession}
          disabled={extending}
          className="gap-1.5 flex-shrink-0"
        >
          <RefreshCw className={`h-4 w-4 ${extending ? 'animate-spin' : ''}`} />
          {t('auth.extendSession')}
        </Button>
        <button
          onClick={onDismiss}
          className="ml-1 p-1 rounded-md text-[var(--text-secondary)] hover:text-[var(--text-color)] hover:bg-[var(--menu-item-hover)] transition-colors flex-shrink-0"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
